import { useCallback, useRef, useState } from 'react'
import {
  allowedRange,
  type AllowedDays,
  type DateRangeEnd,
  type DateRangeValue,
  type RangeLimits,
} from './dateRange'

export type RangeEndProps = AllowedDays & {
  onErrorChange: (message: string | null) => void
}

const otherEnd = (end: DateRangeEnd): DateRangeEnd =>
  end === 'from' ? 'to' : 'from'

/**
 * Props for the two date fields of a range: the days each may take and the
 * error watch. The end the user changed last, while it holds an error, is left
 * out of the other end's bounds, so one wrong date marks one field and not both.
 */
export function useRangeLimits(range: DateRangeValue, limits: RangeLimits) {
  const [errors, setErrors] = useState({ from: false, to: false })
  const previous = useRef(range)
  const lastChanged = useRef<DateRangeEnd | null>(null)
  for (const end of ['from', 'to'] as const) {
    if (previous.current[end]?.getTime() !== range[end]?.getTime())
      lastChanged.current = end
  }
  previous.current = range

  const watch = (end: DateRangeEnd) => (message: string | null) => {
    const failed = message !== null
    setErrors(current =>
      current[end] === failed ? current : { ...current, [end]: failed }
    )
  }
  const watchFrom = useCallback(watch('from'), [])
  const watchTo = useCallback(watch('to'), [])

  // MUI X revalidates when a bound changes identity, so equal days share one Date.
  const stable = useRef(new Map<number, Date>())
  const keep = (date?: Date) => {
    if (!date) return undefined
    const key = date.getTime()
    if (!stable.current.has(key)) stable.current.set(key, date)
    return stable.current.get(key)
  }

  const propsFor = (
    end: DateRangeEnd,
    onErrorChange: RangeEndProps['onErrorChange']
  ): RangeEndProps => {
    const other = otherEnd(end)
    const ignored = errors[other] && lastChanged.current === other
    const seen = ignored ? { ...range, [other]: null } : range
    const { minDate, maxDate } = allowedRange(end, seen, limits)
    return { minDate: keep(minDate), maxDate: keep(maxDate), onErrorChange }
  }

  return {
    from: propsFor('from', watchFrom),
    to: propsFor('to', watchTo),
    /** True while either field holds a date the limits refuse. */
    hasError: errors.from || errors.to,
  }
}
